import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { User, Mail, Lock } from "lucide-react";
import axios from "axios";

const CompanyLandingPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("register");
  
  // Form states
  const [registerForm, setRegisterForm] = useState({ name: "", email: "", password: "" });
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });

  useEffect(() => {
    // Redirect if already logged in
    if (user) {
      if (user.role === "company_admin") navigate("/company-admin");
      else navigate("/dashboard");
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await axios.get(`${API}/companies/slug/${slug}`);
        setCompany(response.data);
        
        // Apply company theme
        const theme = response.data.theme || "light";
        document.documentElement.classList.remove("light", "dark");
        document.documentElement.classList.add(theme);
      } catch (err) {
        setError(err.response?.data?.detail || "Company not found");
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
    
    return () => {
      // Reset theme on unmount
      document.documentElement.classList.remove("light", "dark");
      document.documentElement.classList.add("light");
    };
  }, [slug]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerForm.name || !registerForm.email || !registerForm.password) {
      toast.error("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        name: registerForm.name,
        email: registerForm.email,
        password: registerForm.password,
        company_slug: slug
      });
      
      localStorage.setItem("token", response.data.access_token);
      toast.success(`Welcome to ${company.name}!`);
      
      // Reload to trigger auth context update
      window.location.href = "/dashboard";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    try {
      const userData = await login(loginForm.email, loginForm.password);
      toast.success("Welcome back!");
      if (userData.role === "company_admin") navigate("/company-admin");
      else navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6">
        <h1 className="text-2xl font-bold mb-2">Company Not Found</h1>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Button onClick={() => navigate("/")}>Go to Main Site</Button>
      </div>
    );
  }

  const primaryColor = company?.primary_color || "#4F46E5";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Company Header */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="w-full py-8"
        style={{ 
          background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}05 100%)`
        }}
      >
        <div className="container mx-auto px-6 flex justify-center">
          {company?.logo_url ? (
            <img 
              src={company.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${company.logo_url}` : company.logo_url}
              alt={company.name}
              className="h-16 w-auto"
            />
          ) : (
            <div 
              className="h-16 w-16 rounded-2xl flex items-center justify-center text-white text-2xl font-bold"
              style={{ backgroundColor: primaryColor }}
            >
              {company?.name?.charAt(0) || "C"}
            </div>
          )}
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="shadow-xl border-0 bg-card/80 backdrop-blur">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl">{company?.welcome_title || `Join ${company?.name}`}</CardTitle>
              <CardDescription>{company?.welcome_subtitle || "Create your digital business card"}</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="register" data-testid="company-register-tab">Register</TabsTrigger>
                  <TabsTrigger value="login" data-testid="company-login-tab">Login</TabsTrigger>
                </TabsList>
                
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="register-name"
                          data-testid="company-register-name-input"
                          placeholder="John Doe"
                          className="pl-10 h-12"
                          value={registerForm.name}
                          onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="register-email"
                          data-testid="company-register-email-input"
                          type="email"
                          placeholder="john@example.com"
                          className="pl-10 h-12"
                          value={registerForm.email}
                          onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="register-password"
                          data-testid="company-register-password-input"
                          type="password"
                          placeholder="Create a password"
                          className="pl-10 h-12"
                          value={registerForm.password}
                          onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full h-12 rounded-full"
                      style={{ backgroundColor: primaryColor }}
                      disabled={submitting}
                      data-testid="company-register-submit-btn"
                    >
                      {submitting ? "Creating account..." : "Join Team"}
                    </Button>
                  </form>
                </TabsContent>
                
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="login-email"
                          data-testid="company-login-email-input"
                          type="email"
                          placeholder="john@example.com"
                          className="pl-10 h-12"
                          value={loginForm.email}
                          onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="login-password"
                          data-testid="company-login-password-input"
                          type="password"
                          placeholder="Your password"
                          className="pl-10 h-12"
                          value={loginForm.password}
                          onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full h-12 rounded-full"
                      style={{ backgroundColor: primaryColor }}
                      disabled={submitting}
                      data-testid="company-login-submit-btn"
                    >
                      {submitting ? "Signing in..." : "Sign In"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Company branding notice */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            Your profile will use {company?.name}'s branding
          </p>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="py-4 text-center text-sm text-muted-foreground border-t">
        Powered by h75c
      </footer>
    </div>
  );
};

export default CompanyLandingPage;
